import React, { useState } from "react";
import { toast } from "sonner";
import { Play, Percent, ClipboardCheck, Wrench } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtEUR, L } from "@/constants/labels";
import { Modal, Motif } from "./Modal";

const T = L.versements;

/** Boutons de déclaration côté artisan (acompte / situation / réception / levée). */
export const ArtisanActions = ({ chaine, versements, onChanged }) => {
  const [modal, setModal] = useState(null);
  const pending = versements.find((v) => ["declare", "en_attente_validation", "partiel_propose"].includes(v.statut) && v.type !== "retenue");
  const acompte = versements.find((v) => v.type === "acompte" && v.statut !== "conteste");
  const solde = versements.find((v) => v.type === "solde" && v.statut === "valide_attente_liberation");
  const retenue = versements.find((v) => v.type === "retenue" && v.statut === "retenue_constituee" && v.reserves?.some((r) => r.statut === "ouverte"));
  if (pending) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="artisan-actions">
      {chaine.acompte_pct > 0 && !acompte && !solde && (
        <button className="btn-primary text-xs" onClick={() => setModal("acompte")} data-testid="declarer-demarrage-btn"><Play size={12} /> {T.declarerDemarrage}</button>
      )}
      {!solde && chaine.cumul_engage_pct < 100 && (
        <button className="btn-outline text-xs" onClick={() => setModal("situation")} data-testid="declarer-situation-btn"><Percent size={12} /> {T.declarerSituation}</button>
      )}
      {!solde && (
        <button className="btn-accent text-xs" onClick={() => setModal("reception")} data-testid="demander-reception-btn"><ClipboardCheck size={12} /> {T.demanderReception}</button>
      )}
      {retenue && (
        <button className="btn-outline text-xs" onClick={() => setModal("levee")} data-testid="lever-reserves-btn"><Wrench size={12} /> {T.leverReserves}</button>
      )}
      {modal === "acompte" && <DemarrageModal chaine={chaine} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
      {modal === "situation" && <SituationModal chaine={chaine} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
      {modal === "reception" && <ReceptionDemandeModal chaine={chaine} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
      {modal === "levee" && <LeveeModal retenue={retenue} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
    </div>
  );
};

const useSubmit = (fn, onDone) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await fn(); onDone(); } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };
  return [busy, go];
};

const Footer = ({ onClose, busy, onGo, label, testid, disabled }) => (
  <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
    <button className="btn-ghost" onClick={onClose}>Annuler</button>
    <button className="btn-primary" disabled={busy || disabled} onClick={onGo} data-testid={testid}>{busy ? "…" : label}</button>
  </div>
);

const DemarrageModal = ({ chaine, onClose, onDone }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [commentaire, setCommentaire] = useState("");
  const [busy, go] = useSubmit(async () => {
    await api.post(`/commandes/${chaine.commande_id}/versements/acompte`, { date_demarrage: date, commentaire: commentaire || null, photos: [] });
    toast.success("Démarrage déclaré — votre donneur d'ordre a 48 h ouvrées pour confirmer");
  }, onDone);
  const montant = (chaine.commande_ht * chaine.acompte_pct) / 100;
  return (
    <Modal title={T.declarerDemarrage} onClose={onClose} testid="demarrage-modal">
      <p className="text-[#3E4C59]">Acompte {chaine.acompte_pct} % = <b>{fmtEUR(montant)}</b> HT. Libéré uniquement après confirmation explicite du donneur d'ordre.</p>
      <div><label className="label">Date de démarrage</label><input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="demarrage-date" /></div>
      <div><label className="label">Commentaire (facultatif)</label><textarea className="textarea" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></div>
      <Footer onClose={onClose} busy={busy} onGo={go} label="Déclarer le démarrage" testid="demarrage-submit" />
    </Modal>
  );
};

const SituationModal = ({ chaine, onClose, onDone }) => {
  const reste = Math.max(0, 100 - chaine.cumul_engage_pct);
  const [pct, setPct] = useState(Math.min(reste, 20));
  const [commentaire, setCommentaire] = useState("");
  const [busy, go] = useSubmit(async () => {
    await api.post(`/commandes/${chaine.commande_id}/versements/situation`, { pct: Number(pct), commentaire: commentaire || null, photos: [] });
    toast.success("Situation déclarée — confirmation attendue sous 48 h ouvrées");
  }, onDone);
  const trop = Number(pct) > reste + 1e-9;
  return (
    <Modal title={T.declarerSituation} onClose={onClose} testid="situation-modal">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-[#F5F6F7] rounded p-2">Déjà validé<br /><b className="text-base" data-testid="situation-cumul">{chaine.cumul_valide_pct} %</b> · {fmtEUR(chaine.cumul_valide_ht)}</div>
        <div className="bg-[#F5F6F7] rounded p-2">Reste déclarable<br /><b className="text-base" data-testid="situation-reste">{reste} %</b> · {fmtEUR((chaine.commande_ht * reste) / 100)}</div>
      </div>
      <div><label className="label">Avancement de cette situation (%)</label>
        <input type="number" min="0.5" max={reste} step="0.5" className="input text-lg" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="situation-pct" />
        <div className="text-xs text-[#3E4C59] mt-1">= <b>{fmtEUR((chaine.commande_ht * Number(pct || 0)) / 100)}</b> HT</div>
        {trop && <div className="text-xs text-[#C0392B] mt-1" data-testid="situation-depasse">Le cumul dépasserait 100 % de la commande : un avenant du donneur d'ordre est obligatoire.</div>}
      </div>
      <div><label className="label">Commentaire (facultatif)</label><textarea className="textarea" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></div>
      <Footer onClose={onClose} busy={busy} onGo={go} label="Déclarer la situation" testid="situation-submit" disabled={!pct || Number(pct) <= 0 || trop} />
    </Modal>
  );
};

const ReceptionDemandeModal = ({ chaine, onClose, onDone }) => {
  const [commentaire, setCommentaire] = useState("");
  const [busy, go] = useSubmit(async () => {
    await api.post(`/commandes/${chaine.commande_id}/versements/reception`, { commentaire: commentaire || null, photos: [] });
    toast.success("Réception demandée — le donneur d'ordre doit se prononcer sous 48 h ouvrées");
  }, onDone);
  return (
    <Modal title={T.demanderReception} onClose={onClose} testid="reception-demande-modal">
      <p className="text-[#3E4C59]">Solde prévisionnel : <b>{fmtEUR(chaine.solde_previsionnel_ht)}</b> HT{chaine.retenue?.active ? <> — dont retenue de garantie {chaine.retenue.taux} % ({fmtEUR(chaine.retenue.montant_ht)}) constituée à la réception.</> : "."}</p>
      <p className="text-xs text-[#3E4C59]">La réception est l'acte par lequel le donneur d'ordre accepte l'ouvrage, avec ou sans réserves. Un PV de réception signé électroniquement sera généré.</p>
      <div><label className="label">Commentaire (facultatif)</label><textarea className="textarea" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></div>
      <Footer onClose={onClose} busy={busy} onGo={go} label="Demander la réception" testid="reception-demande-submit" />
    </Modal>
  );
};

const LeveeModal = ({ retenue, onClose, onDone }) => {
  const ouvertes = retenue.reserves.filter((r) => r.statut === "ouverte");
  const [ids, setIds] = useState(ouvertes.map((r) => r.id));
  const [commentaire, setCommentaire] = useState("");
  const [busy, go] = useSubmit(async () => {
    await api.post(`/versements/${retenue.id}/lever-reserves`, { reserve_ids: ids, commentaire: commentaire || null, photos: [] });
    toast.success("Levée déclarée — validation du donneur d'ordre attendue sous 48 h ouvrées");
  }, onDone);
  return (
    <Modal title={T.leverReserves} onClose={onClose} testid="levee-modal">
      <p className="text-[#3E4C59]">Retenue de garantie : <b>{fmtEUR(retenue.montant_demande_ht)}</b> HT, libérable dès validation de la levée de toutes les réserves.</p>
      {ouvertes.map((r, i) => (
        <label key={r.id} className="flex items-start gap-2 p-2 border border-[#E4E7EB] rounded">
          <input type="checkbox" checked={ids.includes(r.id)} onChange={(e) => setIds(e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id))} data-testid={`levee-reserve-${i}`} />
          <span>{r.description}</span>
        </label>
      ))}
      <Motif value={commentaire} onChange={setCommentaire} required={false} placeholder="Travaux de reprise réalisés…" testid="levee-commentaire" />
      <Footer onClose={onClose} busy={busy} onGo={go} label="Déclarer levées" testid="levee-submit" disabled={ids.length === 0} />
    </Modal>
  );
};
